// Closest to close — a card for every deal in diligence or under LOI.
//
// The photo is the deal's own, or its Street View capture, signed on mount the
// way the deal page signs it. Aging ("out 58 days") is for the team only: to a
// partner in the room, an LOI's age reads as a deal going stale.
import { useEffect, useState } from "react";
import { getDocumentUrls } from "../../supabase";
import { stageRamp } from "../common/brand";
import { STAGE_LABEL } from "../../services/homeView";

const M = (v) => `$${(v / 1e6).toFixed(1)}M`;

function Building() {
  return (
    <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#f4f4f4" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
      <path d="M3 21h18" /><path d="M5 21V8l7-4 7 4v13" /><path d="M9 21v-5h6v5" /><path d="M9 11h.01M15 11h.01" />
    </svg>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#8a8a8a]">{k}</div>
      <div className="mt-0.5 text-[15px] font-medium text-[#f4f4f4]">{v}</div>
    </div>
  );
}

export default function CloseCards({ rows, ages, present, equityPct, onOpenDeal }) {
  const [photos, setPhotos] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const photoOf = (r) => r.deal.photoPath || r.deal.streetViewPath;
      const byPath = await getDocumentUrls(rows.map(photoOf));
      if (!cancelled) setPhotos(Object.fromEntries(rows.map((r) => [r.id, byPath[photoOf(r)] || null])));
    })();
    return () => { cancelled = true; };
  }, [rows]);

  if (!rows.length) {
    return (
      <div className="rounded-[14px] border border-[#3a3a3a] bg-[#242424] px-6 py-8 text-[13px] text-[#8a8a8a]">
        Nothing is in diligence or under LOI right now.
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {rows.map((r) => (
        <button key={r.id} data-print-block onClick={() => onOpenDeal?.(r.deal)}
          className="flex flex-col overflow-hidden rounded-[14px] border border-[#3a3a3a] bg-[#242424] text-left transition-colors hover:border-[#4a4a4a]">
          <div className="relative flex h-[132px] items-center justify-center border-b border-[#3a3a3a] bg-[linear-gradient(135deg,#2e2e2e,#232323)]">
            {photos[r.id]
              ? <img src={photos[r.id]} alt="" className="absolute inset-0 h-full w-full object-cover" />
              : <Building />}
            <span className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10.5px] font-bold tracking-[0.06em]"
              style={{ background: stageRamp[r.stage], color: "#ffffff" }}>
              {STAGE_LABEL[r.stage].toUpperCase()}
            </span>
            {r.offMarket && (
              <span className="absolute right-3 top-3 rounded-full border border-white/10 bg-black/50 px-2 py-1 text-[10px] font-semibold"
                style={{ color: "#f4f4f4" }}>Off-market</span>
            )}
          </div>

          <div className="flex flex-1 flex-col gap-3 px-[18px] pb-[18px] pt-4">
            <div>
              <div className="text-[16px] font-semibold tracking-[-0.01em] text-[#f4f4f4]">{r.name}</div>
              <div className="mt-0.5 text-[12px] text-[#8a8a8a]">{r.market}</div>
            </div>
            <div className="grid grid-cols-3 gap-x-2 gap-y-2.5">
              <Stat k="Price" v={r.value > 0 ? M(r.value) : "—"} />
              <Stat k="Size" v={r.sf ? `${Math.round(r.sf / 1000)}k SF` : "—"} />
              <Stat k="$/SF" v={r.psf != null ? `$${Math.round(r.psf)}` : "—"} />
              <Stat k="Levered IRR" v={r.irr != null ? `${r.irr.toFixed(1)}%` : "—"} />
              <Stat k="Multiple" v={r.em != null ? `${r.em.toFixed(2)}x` : "—"} />
              <Stat k={`Equity · ${Math.round(equityPct * 100)}%`} v={r.value > 0 ? M(r.value * equityPct) : "—"} />
            </div>
            <div className="mt-auto flex items-center justify-between gap-2 border-t border-[#303030] pt-3 text-[12px] text-[#b4b4b4]">
              <span>{r.stage === "In Diligence" ? "In diligence" : "LOI submitted"}</span>
              {!present && r.stage === "LOI Submitted" && ages[r.id] != null && (
                <span data-print-hide className="text-[#8a8a8a]">out {ages[r.id]} days</span>
              )}
              {!present && r.stage === "In Diligence" && (
                <span data-print-hide className="rounded-full border border-[rgba(200,146,47,0.35)] bg-[rgba(200,146,47,0.12)] px-2 py-0.5 text-[10.5px] font-semibold text-[#c8922f]">
                  Go-hard date not recorded
                </span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
